import {
	DropOverActionList,
	Icon,
	ListItem
} from "client/Components";
import {
	actionComponentFactory,
	computeActionsToRender
} from "client/Components/ActionList/actionsRenderHelpers";
import {
	ItemSelectionKind,
	SelectableItemOptions,
	useLongTouch
} from "client/Hooks";
import {
	FileThumbnailKind,
	getSpriteClassForExtension,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/fileExtensions";
import {
	Fragment,
	MouseEvent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { never } from "client_legacy/ndaw/errors";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import styles from "./DocumentListItem.module.less";

export type DocumentListItemProps<TActionContext> = {
	name: string;
	additionalInformation?: string | ReactElement;
	documentKind: DocumentItemType;
	extension: string | nil;
	isBookmarked?: boolean;
	isInfected?: boolean;
	isInSelectMode: boolean;
	onEnableSelectMode(): void;
	selectable: SelectableItemOptions;
	onOpenItem: () => void;
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
};

export const DocumentListItem = <TActionContext,>(props: DocumentListItemProps<TActionContext>): ReactElement => {
	const {
		name,
		additionalInformation,
		documentKind,
		extension,
		isBookmarked = false,
		isInfected = false,
		isInSelectMode,
		onEnableSelectMode,
		selectable,
		onOpenItem,
		actions,
		actionContext
	} = props;

	const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
	const actionRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const thumbnail = useMemo(
		(): FileThumbnailKind => {
			switch (documentKind)
			{
				case DocumentItemType.OneNoteNotebook:
					return getSpriteClassForExtension(ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION);
				case DocumentItemType.Folder:
					return "folder";
				case DocumentItemType.File:
					return getSpriteClassForExtension(extension ?? "");
				default:
					never(documentKind);
			}
		},
		[documentKind, extension]
	);

	useEffect(
		(): void => {
			if (isInSelectMode) {
				setIsActionsOpen(false);
			}
		},
		[isInSelectMode]
	);

	const longTouchHandler = useCallback(
		(): void => {
			if (selectable) {
				selectable.onSelect(ItemSelectionKind.Override);
				onEnableSelectMode();
			}
		},
		[onEnableSelectMode, selectable]
	);

	const {
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		isActive: isLongTouchActive
	} = useLongTouch({ handler: longTouchHandler });

	const clickHandler = useCallback(
		(args: MouseEvent) => {
			if (isLongTouchActive) {
				args.preventDefault();
				return;
			}

			if (!isInSelectMode) {
				onOpenItem();
			} else {
				if (selectable) {
					selectable.onSelect(ItemSelectionKind.Append);
				}
			}
		},
		[
			isLongTouchActive,
			isInSelectMode,
			onOpenItem,
			selectable
		]
	);

	const contextMenuHandler = useCallback(
		(args: React.MouseEvent) => {
			args.preventDefault();
		},
		[]
	);

	const actionClickHandler = useCallback(
		(): void => {
			setIsActionsOpen(true);
		},
		[]
	);

	const closeActionHandler = useCallback(
		(): void => {
			setIsActionsOpen(false);
		},
		[]
	);

	return (
		<ListItem
			isActive={selectable ? selectable.isSelected : false}
		>
			{isInSelectMode && selectable && (
				<ListItem.Checkbox
					value={selectable.isSelected}
					onChange={() => selectable.onSelect(ItemSelectionKind.Append)}
				/>
			)}

			<ListItem.Content
				onClick={clickHandler}
				onTouchStart={onTouchStart}
				onTouchEnd={onTouchEnd}
				onTouchMove={onTouchMove}
				onContextMenu={contextMenuHandler}
				thumbnail={
					<div className={styles.thumbnailContainer}>
						<Icon
							value={{
								kind: "svg",
								name: thumbnail
							}}
						/>
					</div>
				}
			>
				<ListItem.Row>
					<ListItem.Title>
						{name}
					</ListItem.Title>

					{isInfected && (
						<ListItem.TitleIcon kind="shield-xmark" color="danger" />
					)}

					{isBookmarked && (
						<ListItem.TitleIcon kind="star-filled" color="bookmark" />
					)}
				</ListItem.Row>

				{additionalInformation && (
					<ListItem.SubTitle>
						{additionalInformation}
					</ListItem.SubTitle>
				)}
			</ListItem.Content>

			{!isInSelectMode && (
				<>
					<ListItem.Action
						icon="ellipsis"
						onClick={actionClickHandler}
						ref={actionRef}
						trackingId="actions"
					/>

					{actionRef.current && isActionsOpen && (
						<DocumentListItemActions
							actions={actions}
							context={actionContext}
							target={actionRef.current}
							onClose={closeActionHandler}
							name={name}
						/>
					)}
				</>
			)}
		</ListItem>
	);
};
DocumentListItem.displayName = "DocumentListItem";

type DocumentListItemActionsProps<TContext> = {
	actions: readonly ActionAndOthers<TContext>[];
	context: TContext;
	target: HTMLElement;
	onClose: () => void;
	name: string;
};

export const DocumentListItemActions = <TContext,>(props: DocumentListItemActionsProps<TContext>): ReactElement => {
	const {
		actions,
		context,
		target,
		onClose,
		name
	} = props;

	const actionsToRender: ActionAndOthers<TContext>[] = computeActionsToRender(
		actions,
		context
	);

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			targetAlignment="br"
			contentAlignment="tr"
			menuTitle={name}
		>
			{actionsToRender.map((x) => (
				<Fragment key={x.name}>
					{actionComponentFactory(x, context)}
				</Fragment>
			))}
		</DropOverActionList>
	);
};
DocumentListItemActions.displayName = "DocumentListItemActions";
